import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { Input } from "@/components/ui/input";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useDebounceCallback } from "@/shared/hooks/use-debounce-callback";
import type { KycStatus } from "@/modules/customers/domain/types";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { ContactIcon, SearchIcon } from "lucide-react";
import { useCustomersQuery } from "../api/queries";
import { CustomersTable } from "../ui/CustomersTable";

type CustomersSearch = {
  q?: string;
  kycStatus?: KycStatus;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

const KYC_FILTER_OPTIONS = [
  { value: "all", label: "KYC: ທຸກສະຖານະ" },
  { value: "PENDING", label: "KYC: ລໍຖ້າກວດ" },
  { value: "VERIFIED", label: "KYC: ຢັ້ງຢືນແລ້ວ" },
  { value: "REJECTED", label: "KYC: ບໍ່ຜ່ານ" },
  { value: "NONE", label: "KYC: ຍັງບໍ່ຍື່ນ" },
] as const;

export function CustomersPage() {
  const nav = useNavigate({ from: "/app/customers" });
  const search = useSearch({ from: "/app/customers" }) as CustomersSearch;

  const debouncedSearch = useDebounceCallback((val: string) => {
    nav({
      search: {
        ...search,
        q: val,
      },
    });
  }, 400);

  const params = {
    q: search.q,
    kycStatus: search.kycStatus,
    sortField: (search.sortField ?? "createdAt") as
      | "createdAt"
      | "name"
      | "phone",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useCustomersQuery(params);
  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ລູກຄ້າ",
              to: "/app/customers",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <ContactIcon className="size-5" />
              ລູກຄ້າ
            </h2>
            <p className="text-muted-foreground">
              ຄົ້ນຫາເບີ ຫຼື ຊື່; ເບິ່ງລາຍລະອຽດ, Wallet ແລະ ອໍເດີທີ່ເຄີຍຊື້.
            </p>
          </div>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
            <div className="relative min-w-[220px] max-w-md flex-1">
              <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="ຄົ້ນຫາເບີ ຫຼື ຊື່..."
                defaultValue={search.q ?? ""}
                onChange={(e) => {
                  debouncedSearch(e.target.value);
                }}
              />
            </div>
            <SimpleSelect
              value={search.kycStatus ?? "all"}
              onValueChange={(val) => {
                nav({
                  search: {
                    ...search,
                    offset: 0,
                    kycStatus:
                      val === "all" ? undefined : (val as KycStatus),
                  },
                });
              }}
              placeholder="KYC"
              className="!h-9 w-full sm:w-44"
              options={[...KYC_FILTER_OPTIONS]}
            />
          </div>

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <CustomersTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onView={(id) => nav({ to: "/app/customers/$id", params: { id } })}
            />
          )}
        </div>
      </Main>
    </>
  );
}
